import React, {createContext, useContext, useEffect, useState, useCallback, useMemo} from "react";
import {fetchSettings} from "../services/api";

// Static source of truth. If the DB / PHP API is down, the portfolio still renders
// every section so the visitor sees a complete site. Keys MUST match the admin panel
// writes and what `<Portfolio />` reads from useSettings().isVisible(...).
const DEFAULTS = Object.freeze({
  "sections.greeting":       true,
  "sections.impact":         true,
  "sections.featuredAI":     true,
  "sections.skills":         true,
  "sections.techStack":      true,
  "sections.education":      true,
  "sections.experience":     true,
  "sections.projects":       true,
  "sections.certifications": true,
  "sections.testimonials":   true,
  "sections.resume":         true,
  "sections.contact":        true,
  "sections.contactForm":    true,
  "sections.resumePdf":      true,
});

const SettingsContext = createContext({
  settings: DEFAULTS,
  isVisible: () => true,
  refresh: () => {},
  loaded: false,
  offline: false,
});

export const SettingsProvider = ({children}) => {
  // Start with full defaults so the FIRST render already paints a usable site —
  // even if the API call never resolves.
  const [settings, setSettings] = useState({...DEFAULTS});
  const [loaded, setLoaded]     = useState(false);
  const [offline, setOffline]   = useState(false);

  const refresh = useCallback(async () => {
    // Defensive: wrap the whole thing. Anything that escapes here (network error,
    // JSON parse, unexpected shape) MUST NOT break the React tree. We swallow and
    // keep showing defaults — the user never sees a crash.
    try {
      const res = await fetchSettings();
      if (res && res.settings && typeof res.settings === "object") {
        // Merge over defaults so newly-added keys (not yet seeded in DB) still resolve.
        setSettings({...DEFAULTS, ...res.settings});
        setOffline(!!res.offline);
      } else {
        // API reachable but returned null/offline → keep defaults, mark offline.
        setSettings({...DEFAULTS});
        setOffline(true);
      }
    } catch (err) {
      // Network error, timeout, CORS, 500 — none should break the UI.
      // eslint-disable-next-line no-console
      console.warn("[SettingsContext] API unreachable, using static defaults", err?.message);
      setSettings({...DEFAULTS});
      setOffline(true);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isVisible = useCallback(
    key => {
      // Unknown keys default to visible (fail-open: better to show too much than break).
      const v = settings[key];
      return v === undefined ? true : !!v;
    },
    [settings]
  );

  const value = useMemo(
    () => ({settings, isVisible, refresh, loaded, offline}),
    [settings, isVisible, refresh, loaded, offline]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);

export default SettingsContext;
